import { useMemo } from 'react';
import { FormField } from '@src/components/SmartDataTable/types';
import { useUserOptionRender } from '@src/hooks';
import { MEDICINE_DOSAGE_OPTIONS, MEDICINE_TIMING_OPTIONS } from '../constants';
import { CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Tooltip, message, Popconfirm } from 'antd';
import { MultilineTextAreaField } from '@src/components/shared/fields';
import dayjs from 'dayjs';

export const usePrescriptionFormFields = (patientId: string): FormField[] => {
  const userOptionRender = useUserOptionRender({
    allowedUserTypes: ['doctor'],
    showUserType: true,
    size: 'small',
  });

  // Helper function to ensure proper row states for medicine data
  const ensureProperRowStates = (medicines: any[]) => {
    if (!Array.isArray(medicines)) return [];

    return medicines.map((medicine, index) => ({
      ...medicine,
      id: medicine.id || `medicine_${index}_${Date.now()}`,
      _action: medicine._action || '', // Clean state for existing medicines
      _originalData: medicine._originalData || { ...medicine },
    }));
  };

  return useMemo(
    () => [
      {
        name: 'patient_id',
        label: 'Patient ID',
        type: 'text',
        required: false,
        defaultValue: patientId,
        layout: { span: 0 },
        style: { display: 'none' },
        hidden: true,
      },
      // Row 1: Prescription Date and Doctor
      {
        name: 'prescribed_at',
        label: 'Prescription Date',
        type: 'datetime',
        required: true,
        defaultValue: (() => {
          const now = dayjs();
          return now.second(0).millisecond(0);
        })(),
        layout: { span: 12 },
        props: {
          size: 'large',
          showTime: true,
          showShortcuts: false,
          format: 'YYYY-MM-DD hh:mm A',
          showNow: true,
          showToday: true,
          minuteStep: 15,
          use12Hours: true,
          placeholder: 'Select date and time',
        },
        tooltip: 'When was this prescription written?',
      },
      {
        name: 'prescribed_by',
        label: 'Doctor',
        type: 'resourceSelect',
        required: true,
        placeholder: 'Search and select doctor...',
        layout: { span: 12 },
        props: {
          size: 'large',
          showSearch: true,
          optionFilterProp: 'children',
          loading: false,
          allowClear: true,
        },
        searchSelectProps: {
          resource: 'users',
          optionValue: 'id',
          searchFields: ['first_name', 'last_name'],
          searchDebounceMs: 300,
          pagination: { pageSize: 20 },
          filters: [
            {
              field: 'status',
              operator: 'eq',
              value: 'active',
            },
            {
              field: 'type',
              operator: 'eq',
              value: 'doctor',
            },
          ],
          sorters: [
            {
              field: 'first_name',
              order: 'asc',
            },
          ],
          optionRender: userOptionRender,
          autoSelectFirst: true,
          queryOptions: {
            enabled: true,
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 10 * 60 * 1000, // 10 minutes
            refetchOnWindowFocus: false,
            refetchOnMount: true,
          },
        },
        tooltip: 'Select the prescribing doctor',
      },

      // Row 2: Notes, Lab Report, Today's Procedure
      {
        name: 'notes',
        label: 'Notes',
        type: 'custom',
        required: false,
        placeholder: 'Enter prescription notes...',
        layout: { span: 8 },
        component: MultilineTextAreaField,
        props: {
          rows: 4,
          showCount: true,
          maxLength: 200,
          autoSize: { minRows: 3, maxRows: 3 },
          style: {
            resize: 'vertical',
            fontFamily: 'monospace',
            lineHeight: '1.5',
          },
        },
        tooltip:
          'Add any general notes about the prescription. Use Enter for new lines.',
      },
      {
        name: 'lab_report',
        label: 'Lab Report',
        type: 'custom',
        required: false,
        placeholder: 'Enter lab report details...',
        layout: { span: 8 },
        component: MultilineTextAreaField,
        props: {
          rows: 4,
          showCount: true,
          maxLength: 200,
          autoSize: { minRows: 3, maxRows: 3 },
          style: {
            resize: 'vertical',
            fontFamily: 'monospace',
            lineHeight: '1.5',
          },
        },
        tooltip:
          'Add any lab report details or findings. Use Enter for new lines.',
      },
      {
        name: 'today_procedure',
        label: "Today's Procedure",
        type: 'custom',
        required: false,
        placeholder: 'Enter procedure details...',
        layout: { span: 8 },
        component: MultilineTextAreaField,
        props: {
          rows: 4,
          showCount: true,
          maxLength: 200,
          autoSize: { minRows: 3, maxRows: 3 },
          style: {
            resize: 'vertical',
            fontFamily: 'monospace',
            lineHeight: '1.5',
          },
        },
        tooltip:
          'Describe any procedures performed today. Use Enter for new lines.',
      },
      // Row 3: Medicine Table
      {
        name: 'prescription_medicines',
        label: 'Medicines',
        type: 'table',
        required: true,
        tableProps: {
          title: '',
          addButtonText: 'Add Medicine',
          minRows: 1,
          showRowNumbers: true,
          showAddButton: true,
          addButtonPosition: 'footer',
          showRowStates: true, // Enable visual row state indicators
          customActions: [
            {
              key: 'copy',
              icon: <CopyOutlined />,
              label: 'Copy Medicine',
              tooltip: 'Copy Medicine',
              onClick: (record: any, index: number, form: any) => {
                try {
                  // Get current medicines array
                  const currentMedicines =
                    form.getFieldValue('prescription_medicines') || [];

                  // Create a copy of the current record with a new unique ID and proper state
                  const copiedMedicine = {
                    ...record,
                    id: `copied_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Generate unique ID
                    _action: 'create' as const, // Set as copied row to show green background
                    _originalData: { ...record }, // Store original data for comparison
                    // Remove any internal form fields that shouldn't be copied
                    _uniqueId: undefined,
                    _copyOperationId: undefined,
                  };

                  // Add the copied medicine to the array
                  const updatedMedicines = [
                    ...currentMedicines,
                    copiedMedicine,
                  ];

                  // Update the form field
                  form.setFieldValue(
                    'prescription_medicines',
                    updatedMedicines
                  );

                  // Show success message
                  message.success(
                    `Medicine "${record.name || 'Item'}" copied successfully!`
                  );
                } catch (error) {
                  console.error('Error copying medicine:', error);
                  message.error('Failed to copy medicine. Please try again.');
                }
              },
            },
          ],
          columns: [
            {
              key: 'medicine_id',
              title: 'ID',
              dataIndex: 'medicine_id',
              defaultValue: null,
              hidden: true,
              field: {
                name: 'medicine_id',
                label: 'Medicine ID',
                type: 'resourceSelect',
                required: false,
                searchSelectProps: {
                  resource: 'medicines',
                  optionValue: 'id',
                  searchFields: ['name'],
                  searchDebounceMs: 300,
                  pagination: { pageSize: 20 },
                  filters: [
                    {
                      field: 'is_active',
                      operator: 'eq',
                      value: 1,
                    },
                  ],
                  sorters: [
                    {
                      field: 'name',
                      order: 'asc',
                    },
                  ],
                  queryOptions: {
                    enabled: true,
                    staleTime: 30 * 1000,
                    cacheTime: 2 * 60 * 1000,
                    refetchOnWindowFocus: false,
                    refetchOnMount: false,
                  },
                },
              },
            },
            {
              key: 'name',
              title: 'Medicine Name',
              dataIndex: 'name',
              width: 200,
              field: {
                name: 'name',
                label: 'Medicine',
                type: 'resourceAutoComplete',
                required: true,
                placeholder: 'Search medicine...',
                autoCompleteProps: {
                  resource: 'medicines',
                  optionValue: 'name',
                  searchFields: ['name'],
                  searchDebounceMs: 300,
                  pagination: { pageSize: 20 },
                  filters: [
                    {
                      field: 'is_active',
                      operator: 'eq',
                      value: 1,
                    },
                  ],
                  sorters: [
                    {
                      field: 'name',
                      order: 'asc',
                    },
                  ],
                  optionRender: (option: any) => option?.name,
                  // Disable React Query cache so same search term always triggers fresh API call.
                  // Fixes: type "clinsure" multiple times → no new request → dropdown shows old results.
                  queryOptions: {
                    enabled: true,
                    staleTime: 0, // never treat as fresh — always refetch on same query
                    cacheTime: 0, // don't cache — avoids serving stale dropdown options
                    refetchOnWindowFocus: false,
                    refetchOnMount: false,
                  },
                },
              },
              onValueChange: (value, rowData, allRows, form) => {
                if (value) {
                  const updatedRow = {
                    ...rowData,
                    medicine_id: value.data?.id || null,
                    quantity: value.data?.pack_quantity || 1,
                    name: value.value,
                    // Keep the original action state - don't change it
                    _action: rowData._action,
                    _originalData: rowData._originalData || { ...rowData },
                  };

                  const rowIndex = allRows.findIndex(
                    row => row.id === rowData.id
                  );
                  if (rowIndex !== -1) {
                    // Update the existing row in place
                    allRows[rowIndex] = updatedRow;
                    form.setFields([
                      { name: 'prescription_medicines', value: [...allRows] },
                    ]);
                  }
                }
              },
            },
            {
              key: 'dosage',
              title: 'Dosage',
              dataIndex: 'dosage',
              width: 150,
              field: {
                name: 'dosage',
                label: 'Dosage',
                type: 'select',
                required: false,
                placeholder: 'Select dosage...',
                options: MEDICINE_DOSAGE_OPTIONS,
                props: {
                  showSearch: true,
                  optionFilterProp: 'label',
                },
              },
            },
            {
              key: 'when_take',
              title: 'When',
              dataIndex: 'when_take',
              width: 150,
              field: {
                name: 'when_take',
                label: 'When',
                type: 'customAutoComplete',
                required: false,
                placeholder: 'Search timing options or type custom...',
                options: MEDICINE_TIMING_OPTIONS,
                props: {
                  maxLength: 100,
                  showCount: true,
                  style: { width: '100%' },
                  allowClear: true,
                },
              },
            },
            {
              key: 'where_take',
              title: 'Where',
              dataIndex: 'where_take',
              width: 150,
              field: {
                name: 'where_take',
                label: 'Where',
                type: 'text',
                required: false,
                placeholder: 'Enter where to take...',
                props: {
                  maxLength: 50,
                },
              },
            },
            {
              key: 'qty',
              title: 'Quantity',
              dataIndex: 'qty',
              width: 100,
              field: {
                name: 'qty',
                label: 'Quantity',
                type: 'number',
                defaultValue: 1,
                required: true,
                placeholder: 'Enter qty...',
                validation: {
                  min: 1,
                  message: 'Please enter a valid quantity',
                },
              },
            },
            {
              key: 'instruction',
              title: 'Remarks',
              dataIndex: 'instruction',
              width: 300,
              field: {
                name: 'instruction',
                label: 'Remarks',
                type: 'custom',
                required: false,
                placeholder: 'Enter remarks...\nUse multiple lines if needed.',
                component: MultilineTextAreaField,
                props: {
                  rows: 2,
                  maxLength: 100,
                  autoSize: { minRows: 1, maxRows: 3 },
                  style: {
                    resize: 'vertical',
                    fontSize: '12px',
                    lineHeight: '1.4',
                  },
                },
              },
            },
          ],
        },
        layout: { span: 24 },
      },
    ],
    [userOptionRender]
  );
};
